import * as Y from 'yjs';

import { WrappedYType } from './types';

export abstract class SynchronizedRecord implements WrappedYType {
  #cache: Map<string, any> = new Map();
  #data: Y.Map<unknown>;
  #attach?: (data: Y.Map<unknown>) => void;

  protected constructor(data: Y.Map<unknown>, attach?: (data: Y.Map<unknown>) => void) {
    data.observe(this.onMapValueChange);
    this.#data = data;
    this.#attach = attach;
  }

  private get(key: string): unknown {
    const cachedValue = this.#cache.get(key);

    if (cachedValue) {
      return cachedValue;
    }

    if (this.#data.has(key)) {
      return this.#data.get(key);
    }

    // eslint-disable-next-line no-underscore-dangle
    return (this.#data as any)._prelimContent?.get(key);
  }

  private memoised<T, TR extends any[]>(
    callback: (key: string, ...args: TR) => T
  ): (key: string, ...rest: TR) => T {
    return (key: string, ...rest: TR) => {
      const existingValue = this.#cache.get(key);

      if (existingValue) {
        return existingValue;
      }

      const result = callback(key, ...rest);

      this.#cache.set(key, result);

      return result;
    };
  }

  /**
   * Sets the given key to the given value.
   *
   * @param key The key to set.
   * @param value The value to set.
   * @param isEqual A function which returns true if the old and new values are equal,
   *   false otherwise. Skips updating the value if it hasn't changed. Defaults to strict
   *   (===) equality.
   */
  protected set<T>(key: string, value: T, isEqual = (a?: T, b?: T) => a === b): void {
    if (this.#attach) {
      this.#attach(this.#data);
    }
    const oldValue = this.get(key) as T;
    if (!isEqual(oldValue, value)) {
      this.#data.set(key, value);
    }
  }

  protected getNumber = this.memoised((key: string, defaultValue) => {
    const value: unknown = this.get(key);

    if (typeof value !== 'number' || isNaN(value)) {
      return defaultValue;
    }

    this.#cache.set(key, value);

    return value;
  });

  protected getString = this.memoised(<T>(key: string, defaultValue: T): string | T => {
    const value: unknown = this.get(key);

    if (typeof value !== 'string') {
      return defaultValue;
    }

    return value;
  });

  protected getBoolean = this.memoised(<T>(key: string, defaultValue: T): boolean | T => {
    const value: unknown = this.get(key);

    if (typeof value !== 'boolean') {
      return defaultValue;
    }

    return value;
  });

  protected getStringEnum = this.memoised(
    <T extends readonly string[]>(
      key: string,
      allowedValues: T,
      defaultValue: T[number]
    ): T[number] => {
      const value = this.get(key);

      if (typeof value !== 'string' || !allowedValues.includes(value)) {
        return defaultValue;
      }

      return value;
    }
  );

  protected getStringEnumOptional = this.memoised(
    <T extends readonly string[]>(
      key: string,
      allowedValues: T,
      defaultValue: T[number] | undefined
    ): T[number] | undefined => {
      const value = this.get(key);

      if (typeof value !== 'string' || !allowedValues.includes(value)) {
        return defaultValue;
      }

      return value;
    }
  );

  protected getStringEnumArray = this.memoised(
    <T extends readonly string[]>(
      key: string,
      allowedValues: T,
      defaultValue: T
    ): string[] | T => {
      const value = this.get(key) as string[];

      if (!Array.isArray(value)) {
        return defaultValue;
      }

      for (let v = 0; v < value.length; v++) {
        if (!allowedValues.includes(value[v])) {
          return defaultValue;
        }
      }

      return value;
    }
  );

  protected getObject = this.memoised(<T>(key: string, defaultValue: T): T | object => {
    const value = this.get(key) as Record<string, unknown>;

    if (typeof value !== 'object') {
      return defaultValue;
    }

    return value;
  });

  protected getText = this.memoised((key: string): Y.Text | undefined => {
    const value = this.get(key);
    if (value instanceof Y.Text) {
      return value;
    }
  });

  private onMapValueChange = (ev: Y.YMapEvent<unknown>) => {
    ev.keysChanged.forEach((key) => this.#cache.delete(key));
  };

  get data(): Y.Map<unknown> {
    return this.#data;
  }

  set data(value: Y.Map<unknown>) {
    this.#data.unobserve(this.onMapValueChange);
    this.#data = value;
    this.#cache.clear();
    this.#data.observe(this.onMapValueChange);
  }

  unwrap(): Y.AbstractType<any> {
    return this.#data;
  }

  dispose(): void {
    this.#data.unobserve(this.onMapValueChange);
  }
}
